<!-- components/DesktopHeader.vue -->
<script setup lang="ts">
import { computed } from "vue";
import { useAssetsImgs } from "@/hook/useAssetsImgs";
import { useLocale } from "@/hook/useLocale";

import type { NavigationItem } from "./type";

const { currentLocale, localeLabel, toggleLocale, loginLabel } = useLocale();
const isEnglish = computed(() => currentLocale.value === "en");

interface Props {
  logoPath?: string;
  logoAlt?: string;
  logoHeight?: string;
  mainMenuItems?: NavigationItem[];
  loginPath?: string;
}

const props = withDefaults(defineProps<Props>(), {
  logoPath: "@/assets/img/logo_column.png",
  logoAlt: "logo",
  logoHeight: "h-25 max-[639px]:h-15 md:h-15",
  loginPath: "/login",
  mainMenuItems: () => [],
});

const { imageUrl } = useAssetsImgs(props.logoPath);

const emit = defineEmits<{
  (e: "menu-click", item: NavigationItem): void;
  (e: "logo-click"): void;
}>();

const getMenuItemPath = (item: NavigationItem): string => {
  return item.href || item.to || item.path || "/";
};

const handleMenuClick = (item: NavigationItem) => {
  emit("menu-click", item);
};

const handleLogoClick = () => {
  emit("logo-click");
};

const handleLanguageToggle = (e: Event) => {
  e.preventDefault();
  toggleLocale();
};
</script>

<template>
  <header
    class="fixed top-0 bg-white/50 backdrop-blur-md left-0 z-50 hidden w-full shadow-md xl:flex"
  >
    <nav
      :class="[
        'container mx-auto flex w-full max-w-md justify-between md:max-w-max',
        isEnglish ? 'px-3 2xl:px-6' : 'px-6',
      ]"
    >
      <!-- Logo -->
      <div class="col-auto shrink-0">
        <div class="header-logo">
          <router-link to="/" @click="handleLogoClick">
            <img
              :src="imageUrl"
              :class="logoHeight"
              :alt="logoAlt"
              width="44px"
              :height="logoHeight"
            />
          </router-link>
        </div>
      </div>

      <!-- 主菜单 -->
      <ul
        :class="[
          'main-menu',
          isEnglish ? 'gap-2 2xl:gap-8' : 'gap-4 2xl:gap-10',
        ]"
      >
        <template v-for="item in mainMenuItems" :key="item.id">
          <!-- 有子菜单 -->
          <li
            v-if="item.children && item.children.length"
            class="shrink-0"
          >
            <a
              :href="getMenuItemPath(item)"
              class="shrink-0 whitespace-nowrap bg-transparent text-[16px] text-[#373e43] hover:text-[#c57642]"
              @click="handleMenuClick(item)"
            >
              {{ item.label }}
            </a>
            <div class="desktop-submenu">
              <ul class="w-62.5 p-2">
                <li v-for="child in item.children" :key="child.id">
                  <a
                    :href="getMenuItemPath(child)"
                    class="hover:bg-muted mb-1 flex w-full gap-2 rounded-md px-3 py-2 transition-all"
                    @click="handleMenuClick(child)"
                  >
                    {{ child.label }}
                  </a>
                </li>
              </ul>
            </div>
          </li>

          <!-- 普通菜单项 -->
          <li v-else class="shrink-0">
            <a
              :href="getMenuItemPath(item)"
              class="shrink-0 whitespace-nowrap"
              @click="handleMenuClick(item)"
            >
              {{ item.label }}
            </a>
          </li>
        </template>
      </ul>

      <!-- 右侧菜单 -->
      <ul class="main-menu gap-2">
        <li class="shrink-0">
          <a href="#"
            class="shrink-0 whitespace-nowrap"
            @click="handleLanguageToggle"
          >
            {{ localeLabel }}
          </a>
        </li>
        <div class="h-7.5 border-l border-[#373e43]"></div>
        <li class="shrink-0">
          <a
            :href="loginPath"
            class="shrink-0 whitespace-nowrap"
          >
            {{ loginLabel }}
          </a>
        </li>
      </ul>
    </nav>
  </header>
</template>

<style scoped>
header {
  padding-right: var(--scrollbar-width);
}

.main-menu :deep(a),
.main-menu :deep(button) {
  display: block;
  position: relative;
  font-weight: 500;
  font-size: 16px;
  color: var(--title-color, #373e43);
  text-transform: uppercase;
  white-space: nowrap;
}

.main-menu :deep(a:hover),
.main-menu :deep(button:hover) {
  color: var(--theme-color, #c57642);
}

.main-menu > ul > li > a {
  padding: 44.5px 0;
}

.main-menu ul {
  margin: 0;
  padding: 0;
}

.main-menu ul li {
  list-style-type: none;
  display: inline-block;
  position: relative;
}

.main-menu {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
}

.main-menu > li {
  position: relative;
  list-style: none;
}

.desktop-submenu {
  position: absolute;
  top: calc(100% - 1px);
  left: 50%;
  min-width: 250px;
  padding-top: 1px;
  visibility: hidden;
  opacity: 0;
  transform: translate(-50%, 8px);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;
}

.desktop-submenu > ul {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 14px 35px rgba(36, 29, 23, 0.12);
}

.main-menu > li:hover .desktop-submenu,
.main-menu > li:focus-within .desktop-submenu {
  visibility: visible;
  opacity: 1;
  transform: translate(-50%, 0);
}

.desktop-submenu li,
.desktop-submenu a {
  display: block !important;
  width: 100%;
}


.header-logo {
  padding-top: 10px;
  padding-bottom: 10px;
}

</style>
